import React from 'react';
import { Instrument, ItemSummary, Scale } from './api';

/**
 * The controls a study asks its question with, chosen from its instrument.
 *
 * Four types, matching the backend's closed set: a score, a multi-dimension rubric, a forced choice
 * between the unit's items, and tagging from a controlled vocabulary. Closed on purpose — an
 * arbitrary form builder would make every study's export bespoke, which is the problem the engine
 * exists to solve.
 *
 * The scale buttons deliberately reuse the shape /rate has always had (a 68px button with the
 * rubric word underneath, dark when chosen), because raters work several studies and a control that
 * looks the same should behave the same.
 */

export type ResponseValue = Record<string, unknown>;

interface Props {
  instrument: Instrument;
  items: ItemSummary[];
  value: ResponseValue;
  onChange: (next: ResponseValue) => void;
  disabled?: boolean;
}

const scalePoints = (scale: Scale): number[] => {
  const points: number[] = [];
  for (let n = scale.min; n <= scale.max; n += 1) points.push(n);
  return points;
};

const ScaleButtons: React.FC<{
  scale: Scale;
  selected: number | null;
  onPick: (value: number) => void;
  disabled?: boolean;
  ariaLabel: string;
}> = ({ scale, selected, onPick, disabled, ariaLabel }) => (
  <div className="flex gap-1.5 flex-wrap" role="radiogroup" aria-label={ariaLabel}>
    {scalePoints(scale).map((point) => {
      const label = scale.labels?.[String(point)];
      return (
        <button
          key={point}
          type="button"
          role="radio"
          aria-checked={selected === point}
          aria-label={label ? `${point} — ${label}` : String(point)}
          onClick={() => onPick(point)}
          disabled={disabled}
          className={`min-w-[68px] min-h-[44px] px-2 py-1 rounded-xl border-[1.5px]
                      flex flex-col items-center justify-center gap-0.5 transition-all
                      hover:-translate-y-px focus:outline-none disabled:opacity-60
                      focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 ${
                        selected === point
                          ? 'bg-gray-900 border-gray-900 text-white'
                          : 'bg-white border-gray-200 text-gray-500 hover:border-blue-600'
                      }`}
        >
          <span className="text-[15px] font-semibold leading-none">{point}</span>
          {label && <span className="text-[9px] leading-none whitespace-nowrap">{label}</span>}
        </button>
      );
    })}
  </div>
);

const InstrumentControls: React.FC<Props> = ({ instrument, items, value, onChange, disabled }) => {
  if (instrument.type === 'scalar' && instrument.scale) {
    return (
      <ScaleButtons
        scale={instrument.scale}
        selected={(value.score as number) ?? null}
        onPick={(score) => onChange({ score })}
        disabled={disabled}
        ariaLabel="Your rating"
      />
    );
  }

  if (instrument.type === 'rubric' && instrument.dimensions) {
    const scores = (value.scores as Record<string, number>) ?? {};
    return (
      <div className="flex flex-col gap-2.5">
        {instrument.dimensions.map((dimension) => (
          <div key={dimension.key} className="flex items-center gap-3 flex-wrap">
            <span className="text-[11px] font-semibold text-gray-600 min-w-[92px]">
              {dimension.label}
            </span>
            <ScaleButtons
              scale={dimension.scale}
              selected={scores[dimension.key] ?? null}
              onPick={(score) => onChange({ scores: { ...scores, [dimension.key]: score } })}
              disabled={disabled}
              ariaLabel={dimension.label}
            />
          </div>
        ))}
      </div>
    );
  }

  if (instrument.type === 'choice') {
    const winner = (value.winner as number) ?? null;
    return (
      <div className="flex gap-2 flex-wrap" role="radiogroup" aria-label="Which is better">
        {items.map((item, index) => (
          <button
            key={item.id}
            type="button"
            role="radio"
            aria-checked={winner === index}
            onClick={() => onChange({ winner: index })}
            disabled={disabled}
            className={`px-4 min-h-[44px] rounded-xl border-[1.5px] text-[13px] font-semibold
                        transition-all disabled:opacity-60 ${
                          winner === index
                            ? 'bg-gray-900 border-gray-900 text-white'
                            : 'bg-white border-gray-200 text-gray-700 hover:border-blue-600'
                        }`}
          >
            {/* Positional label first — the rater is choosing between sides, and the ordering is
                fixed server-side so A stays A. */}
            {String.fromCharCode(65 + index)} — {item.title || item.item_ref} is better
          </button>
        ))}
      </div>
    );
  }

  if (instrument.type === 'tags' && instrument.vocabulary) {
    const tags = (value.tags as string[]) ?? [];
    const toggle = (key: string) => {
      const next = tags.includes(key) ? tags.filter((t) => t !== key) : [...tags, key];
      onChange({ tags: next });
    };
    return (
      <div>
        <div className="flex gap-1.5 flex-wrap" role="group" aria-label="Tags">
          {instrument.vocabulary.map((entry) => {
            const on = tags.includes(entry.key);
            return (
              <button
                key={entry.key}
                type="button"
                aria-pressed={on}
                onClick={() => toggle(entry.key)}
                disabled={disabled}
                className={`px-3 min-h-[36px] rounded-full border-[1.5px] text-[12.5px]
                            font-semibold transition-all disabled:opacity-60 ${
                              on
                                ? 'bg-blue-600 border-blue-600 text-white'
                                : 'bg-white border-gray-200 text-gray-600 hover:border-blue-600'
                            }`}
              >
                {entry.label}
              </button>
            );
          })}
        </div>
        {(instrument.min || instrument.max) && (
          <p className="text-[11px] text-gray-500 mt-1.5">
            Choose {instrument.min ?? 0}–{instrument.max ?? instrument.vocabulary.length}. Selected{' '}
            {tags.length}.
          </p>
        )}
      </div>
    );
  }

  // An instrument this build does not know. Say so rather than rendering nothing — an empty control
  // would look like a page that failed to load, and a rater might report it as broken instead.
  return (
    <p className="text-sm text-gray-700">
      This study uses a <code className="font-mono">{instrument.type}</code> instrument, which this
      version of the app cannot show.
    </p>
  );
};

/** Whether the current answer is complete enough to submit, per the instrument's own rules. */
export function isComplete(instrument: Instrument, value: ResponseValue): boolean {
  if (instrument.type === 'scalar') return typeof value.score === 'number';
  if (instrument.type === 'rubric') {
    const scores = (value.scores as Record<string, number>) ?? {};
    return (instrument.dimensions ?? []).every((d) => typeof scores[d.key] === 'number');
  }
  if (instrument.type === 'choice') return typeof value.winner === 'number';
  if (instrument.type === 'tags') {
    const tags = (value.tags as string[]) ?? [];
    const low = instrument.min ?? 0;
    const high = instrument.max ?? instrument.vocabulary?.length ?? tags.length;
    return tags.length >= low && tags.length <= high;
  }
  return false;
}

/**
 * Whether the written reason a study demands has been given.
 *
 * Lives here beside `isComplete` because the two together are what "this answer is ready to send"
 * means, and the backend rejects a response that fails either. Keeping the remarks half in the
 * bottom bar instead let autosave fire without it: the rater picks a score, pauses to compose their
 * reason, and the debounce PUTs an empty one straight into a 422.
 */
export function hasRequiredRemarks(instrument: Instrument, remarks: string): boolean {
  return instrument.remarks !== 'required' || remarks.trim().length > 0;
}

export default InstrumentControls;
